import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, FileText, Send, Mail, ChevronDown, ChevronUp,
  ArrowLeft, Save, Eye, AlertCircle, ExternalLink, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK_SEGMENT = {
  segment_order: 0,
  departure_airport_code: "",
  departure_city: "",
  arrival_airport_code: "",
  arrival_city: "",
  departure_date: "",
  departure_time: "",
  arrival_date: "",
  arrival_time: "",
  airline: "",
  flight_number: "",
  cabin_class: "Economy",
  notes: "",
};

const BLANK_FORM = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  passengers: [""],
  segments: [{ ...BLANK_SEGMENT }],
  customer_notes: "",
  internal_notes: "",
  service_fee_type: "free",
  service_fee_amount: "",
  service_fee_currency: "EUR",
};

const STATUS_BADGE = {
  draft:     "bg-slate-100 text-slate-600",
  sent:      "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-600",
};

export default function ProvisionalItineraryDetail() {
  const { piId } = useParams();
  const isNew = piId === "new";
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [pi, setPi]         = useState(null);
  const [form, setForm]     = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Invoice creation
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invDate, setInvDate]   = useState(new Date().toISOString().slice(0, 10));
  const [invVat,  setInvVat]    = useState("0% VAT");
  const [invNotes, setInvNotes] = useState("");
  const [invCreating, setInvCreating] = useState(false);

  // Linked invoice details
  const [linkedInvoice, setLinkedInvoice] = useState(null);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provisional-itineraries/${piId}`, { headers: getAuthHeaders() });
      const data = res.data;
      setPi(data);
      setForm({
        customer_name:        data.customer_name       || "",
        customer_email:       data.customer_email      || "",
        customer_phone:       data.customer_phone      || "",
        passengers:           (data.passengers || []).length ? data.passengers : [""],
        segments:             (data.segments   || []).length ? data.segments   : [{ ...BLANK_SEGMENT }],
        customer_notes:       data.customer_notes      || "",
        internal_notes:       data.internal_notes      || "",
        service_fee_type:     data.service_fee_type    || "free",
        service_fee_amount:   data.service_fee_amount  != null ? String(data.service_fee_amount) : "",
        service_fee_currency: data.service_fee_currency || "EUR",
      });
      // Load linked invoice if present
      if (data.linked_invoice_id) {
        try {
          const inv = await axios.get(`${API}/invoices/${data.linked_invoice_id}`, { headers: getAuthHeaders() });
          setLinkedInvoice(inv.data);
        } catch { /* non-critical */ }
      }
    } catch {
      toast.error("Failed to load provisional itinerary");
    } finally {
      setLoading(false);
    }
  }, [piId, isNew, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // ── Form field helpers ────────────────────────────────────────────────────
  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const setSegField = (idx, field, value) =>
    setForm(f => {
      const segs = [...f.segments];
      segs[idx] = { ...segs[idx], [field]: value };
      return { ...f, segments: segs };
    });

  const addSegment = () =>
    setForm(f => ({
      ...f,
      segments: [...f.segments, { ...BLANK_SEGMENT, segment_order: f.segments.length }],
    }));

  const removeSegment = (idx) =>
    setForm(f => ({ ...f, segments: f.segments.filter((_, i) => i !== idx) }));

  const setPax = (idx, value) =>
    setForm(f => {
      const pax = [...f.passengers];
      pax[idx] = value;
      return { ...f, passengers: pax };
    });

  const addPax  = () => setForm(f => ({ ...f, passengers: [...f.passengers, ""] }));
  const rmPax   = (idx) => setForm(f => ({ ...f, passengers: f.passengers.filter((_, i) => i !== idx) }));

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        passengers: form.passengers.filter(p => p.trim()),
        segments: form.segments.map((s, i) => ({ ...s, segment_order: i })),
        service_fee_amount: form.service_fee_type === "paid" && form.service_fee_amount
          ? parseFloat(form.service_fee_amount) : null,
      };
      if (isNew) {
        const res = await axios.post(`${API}/provisional-itineraries`, payload, { headers: getAuthHeaders() });
        toast.success(`Created ${res.data.pi_number}`);
        navigate(`/provisional-itineraries/${res.data.id}`, { replace: true });
      } else {
        await axios.put(`${API}/provisional-itineraries/${piId}`, payload, { headers: getAuthHeaders() });
        toast.success("Saved ✓");
        load();
      }
    } catch (e) {
      toast.error("Save failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // ── PDF ──────────────────────────────────────────────────────────────────
  const openPdf = async () => {
    try {
      const res = await axios.post(`${API}/provisional-itineraries/${piId}/pdf`, {}, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      load();
    } catch {
      toast.error("PDF generation failed");
    }
  };

  // ── Email ────────────────────────────────────────────────────────────────
  const sendEmail = async (isTest) => {
    setEmailSending(true);
    try {
      const res = await axios.post(
        `${API}/provisional-itineraries/${piId}/send-email`,
        { is_test: isTest },
        { headers: getAuthHeaders() },
      );
      toast.success(res.data.message);
      setShowEmailModal(false);
      load();
    } catch (e) {
      toast.error("Email failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setEmailSending(false);
    }
  };

  // ── Create Invoice ────────────────────────────────────────────────────────
  const createInvoice = async () => {
    setInvCreating(true);
    try {
      const res = await axios.post(
        `${API}/provisional-itineraries/${piId}/create-invoice`,
        { invoice_date: invDate, vat_type: invVat, notes: invNotes },
        { headers: getAuthHeaders() },
      );
      toast.success(`Invoice ${res.data.invoice.invoice_number} created`);
      setShowInvoiceModal(false);
      load();
    } catch (e) {
      toast.error("Invoice creation failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setInvCreating(false);
    }
  };

  // ── Cancel / restore ──────────────────────────────────────────────────────
  const toggleCancel = async () => {
    const newStatus = pi?.status === "cancelled" ? "draft" : "cancelled";
    try {
      if (newStatus === "cancelled") {
        await axios.delete(`${API}/provisional-itineraries/${piId}`, { headers: getAuthHeaders() });
      } else {
        await axios.put(`${API}/provisional-itineraries/${piId}`, { status: "draft" }, { headers: getAuthHeaders() });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  // ── Route summary for email preview ─────────────────────────────────────
  const routeSummary = () => {
    const segs = form.segments.filter(s => s.departure_date);
    if (!segs.length) return "";
    const dep = segs[0].departure_airport_code || segs[0].departure_city || "";
    const arr = segs[segs.length - 1].arrival_airport_code || segs[segs.length - 1].arrival_city || "";
    return dep || arr ? `${dep} → ${arr}` : "";
  };

  const tf = "w-full h-9 text-sm border border-slate-200 rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-purple-400";

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-slate-400">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/provisional-itineraries")}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900">
                  {isNew ? "New Provisional Itinerary" : (pi?.pi_number || "Provisional Itinerary")}
                </h1>
                {pi && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[pi.status] || "bg-slate-100 text-slate-500"}`}>
                    {pi.status}
                  </span>
                )}
              </div>
              {!isNew && pi?.reservation_ref && (
                <p className="text-xs text-slate-500 mt-0.5 font-mono">
                  Reservation Reference: <span className="font-semibold text-purple-700">{pi.reservation_ref}</span>
                </p>
              )}
              {!isNew && pi?.created_at && (
                <p className="text-xs text-slate-400 mt-0.5">
                  Created {new Date(pi.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  {pi.email_sent_at && ` · Sent ${new Date(pi.email_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} to ${pi.email_sent_to}`}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {!isNew && (
              <>
                <Button variant="outline" size="sm" onClick={openPdf} className="gap-1.5 text-xs">
                  <FileText className="w-3.5 h-3.5" /> View PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowEmailModal(true)} className="gap-1.5 text-xs">
                  <Mail className="w-3.5 h-3.5" /> Email
                </Button>
              </>
            )}
            <Button onClick={handleSave} disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5 text-xs">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* Disclaimer notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">
            A provisional itinerary is <strong>not</strong> a flight booking. It does not create a PNR, reserve seats,
            or issue a ticket. It will not appear in Flight Bookings or affect VAT reporting unless a paid invoice is explicitly created.
          </p>
        </div>

        <div className="space-y-6">
          {/* ── Customer ────────────────────────────────────────────────── */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Customer</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Customer Name *</Label>
                <Input value={form.customer_name} onChange={e => setField("customer_name", e.target.value)}
                  placeholder="Full name" className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <Input type="email" value={form.customer_email} onChange={e => setField("customer_email", e.target.value)}
                  placeholder="customer@email.com" className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Phone</Label>
                <Input value={form.customer_phone} onChange={e => setField("customer_phone", e.target.value)}
                  placeholder="+31 6 …" className="mt-1 h-9 text-sm" />
              </div>
            </div>
          </section>

          {/* ── Passengers ─────────────────────────────────────────────── */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-700">Passengers</h2>
              <button onClick={addPax} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add passenger
              </button>
            </div>
            <div className="space-y-2">
              {form.passengers.map((p, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={p} onChange={e => setPax(i, e.target.value)}
                    placeholder={`Passenger ${i + 1} full name`}
                    className="h-9 text-sm flex-1" />
                  {form.passengers.length > 1 && (
                    <button onClick={() => rmPax(i)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* ── Flight Segments ─────────────────────────────────────────── */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-700">Flight Segments</h2>
              <button onClick={addSegment} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add segment
              </button>
            </div>
            <div className="space-y-4">
              {form.segments.map((seg, idx) => (
                <div key={idx} className="border border-slate-100 rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Segment {idx + 1}
                    </span>
                    {form.segments.length > 1 && (
                      <button onClick={() => removeSegment(idx)} className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Dep. Airport Code</Label>
                      <Input value={seg.departure_airport_code} onChange={e => setSegField(idx, "departure_airport_code", e.target.value.toUpperCase())}
                        placeholder="AMS" className="mt-1 h-9 text-sm font-mono uppercase" maxLength={4} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Dep. City</Label>
                      <Input value={seg.departure_city} onChange={e => setSegField(idx, "departure_city", e.target.value)}
                        placeholder="Amsterdam" className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Arr. Airport Code</Label>
                      <Input value={seg.arrival_airport_code} onChange={e => setSegField(idx, "arrival_airport_code", e.target.value.toUpperCase())}
                        placeholder="DXB" className="mt-1 h-9 text-sm font-mono uppercase" maxLength={4} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Arr. City</Label>
                      <Input value={seg.arrival_city} onChange={e => setSegField(idx, "arrival_city", e.target.value)}
                        placeholder="Dubai" className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Departure Date</Label>
                      <Input type="date" value={seg.departure_date} onChange={e => setSegField(idx, "departure_date", e.target.value)}
                        className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Departure Time</Label>
                      <Input type="time" value={seg.departure_time} onChange={e => setSegField(idx, "departure_time", e.target.value)}
                        className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Arrival Date</Label>
                      <Input type="date" value={seg.arrival_date} onChange={e => setSegField(idx, "arrival_date", e.target.value)}
                        className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Arrival Time</Label>
                      <Input type="time" value={seg.arrival_time} onChange={e => setSegField(idx, "arrival_time", e.target.value)}
                        className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Airline</Label>
                      <Input value={seg.airline} onChange={e => setSegField(idx, "airline", e.target.value)}
                        placeholder="Emirates" className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Flight Number</Label>
                      <Input value={seg.flight_number} onChange={e => setSegField(idx, "flight_number", e.target.value)}
                        placeholder="EK 147" className="mt-1 h-9 text-sm font-mono" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Cabin / Class</Label>
                      <Select value={seg.cabin_class || "Economy"}
                        onValueChange={v => setSegField(idx, "cabin_class", v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Economy", "Premium Economy", "Business", "First"].map(c =>
                            <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs text-slate-500">Segment Notes (optional)</Label>
                      <Input value={seg.notes} onChange={e => setSegField(idx, "notes", e.target.value)}
                        placeholder="e.g. overnight flight, stopover 2h" className="mt-1 h-9 text-sm" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Notes ───────────────────────────────────────────────────── */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Notes</h2>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Customer-Facing Notes (shown on PDF and email)</Label>
                <textarea
                  value={form.customer_notes}
                  onChange={e => setField("customer_notes", e.target.value)}
                  rows={3}
                  placeholder="Additional information for the customer…"
                  className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Internal Notes (not shown to customer)</Label>
                <textarea
                  value={form.internal_notes}
                  onChange={e => setField("internal_notes", e.target.value)}
                  rows={2}
                  placeholder="Internal reference, supplier info, etc."
                  className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none bg-amber-50"
                />
              </div>
            </div>
          </section>

          {/* ── Service Fee ─────────────────────────────────────────────── */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Service Fee</h2>
            <div className="flex gap-4 mb-4">
              {["free", "paid"].map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="service_fee_type" value={t}
                    checked={form.service_fee_type === t}
                    onChange={() => setField("service_fee_type", t)}
                    className="accent-purple-600" />
                  <span className="text-sm font-medium text-slate-700 capitalize">{t}</span>
                </label>
              ))}
            </div>

            {form.service_fee_type === "paid" && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Amount</Label>
                  <Input type="number" step="0.01" min="0"
                    value={form.service_fee_amount}
                    onChange={e => setField("service_fee_amount", e.target.value)}
                    placeholder="0.00"
                    className="mt-1 h-9 text-sm font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Currency</Label>
                  <Select value={form.service_fee_currency}
                    onValueChange={v => setField("service_fee_currency", v)}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["EUR", "GBP", "USD", "AED"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Invoice section (only when paid + not new) */}
            {form.service_fee_type === "paid" && !isNew && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                {pi?.linked_invoice_id ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Linked Invoice</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono font-semibold text-sm text-slate-800">{pi.linked_invoice_number}</span>
                        {linkedInvoice && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            linkedInvoice.invoice_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                            linkedInvoice.invoice_status === "sent" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-100 text-slate-600"}`}>
                            {linkedInvoice.invoice_status}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm"
                      onClick={() => navigate(`/invoices/${pi.linked_invoice_id}`)}
                      className="gap-1.5 text-xs">
                      <ExternalLink className="w-3 h-3" /> View Invoice
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">No invoice created yet.</p>
                    <Button variant="outline" size="sm"
                      onClick={() => setShowInvoiceModal(true)}
                      className="gap-1.5 text-xs border-purple-200 text-purple-700 hover:bg-purple-50">
                      <Plus className="w-3 h-3" /> Create Invoice
                    </Button>
                  </div>
                )}
              </div>
            )}
            {form.service_fee_type === "paid" && isNew && (
              <p className="text-xs text-slate-400 mt-3">Save the itinerary first, then create the invoice.</p>
            )}
          </section>

          {/* ── Danger zone ─────────────────────────────────────────────── */}
          {!isNew && (
            <section className="bg-white rounded-xl border border-slate-200 p-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Status</h2>
              <div className="flex gap-2">
                {pi?.status !== "cancelled" ? (
                  <Button variant="outline" size="sm" onClick={toggleCancel}
                    className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                    Cancel Itinerary
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={toggleCancel}
                    className="text-xs text-slate-600">
                    Restore to Draft
                  </Button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* ── Email Modal ───────────────────────────────────────────────────── */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Send Itinerary Email</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-4 space-y-2 text-sm">
              <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Subject</span>
                <span className="text-slate-700">Provisional Travel Itinerary {pi?.pi_number || ""}
                  {routeSummary() ? ` — ${routeSummary()}` : ""}</span></div>
              <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Customer</span>
                <span className="text-slate-700">{form.customer_email || <em className="text-slate-400">No email set</em>}</span></div>
              <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Attachment</span>
                <span className="text-slate-700 font-mono text-xs">{pi?.pi_number || "itinerary"}.pdf</span></div>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              <strong>Send to Customer</strong> delivers to the customer email above.<br />
              <strong>Test Email</strong> sends only to the admin address, prefixed [TEST], with no DB updates.
            </p>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowEmailModal(false)} className="text-xs">Cancel</Button>
              <Button variant="outline" size="sm" onClick={() => sendEmail(true)} disabled={emailSending}
                className="text-xs">
                <Mail className="w-3 h-3 mr-1" /> {emailSending ? "Sending…" : "Test Email"}
              </Button>
              <Button size="sm" onClick={() => sendEmail(false)} disabled={emailSending || !form.customer_email}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs gap-1">
                <Send className="w-3 h-3" /> {emailSending ? "Sending…" : "Send to Customer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Invoice Modal ──────────────────────────────────────────── */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Create Invoice</h3>
              <button onClick={() => setShowInvoiceModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500">Invoice Date</Label>
                <Input type="date" value={invDate} onChange={e => setInvDate(e.target.value)} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">VAT Type</Label>
                <Select value={invVat} onValueChange={setInvVat}>
                  <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["0% VAT", "21% VAT", "9% VAT", "Outside Scope EU VAT"].map(v =>
                      <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Invoice Notes (optional)</Label>
                <Input value={invNotes} onChange={e => setInvNotes(e.target.value)}
                  placeholder="Leave blank for default" className="mt-1 h-9 text-sm" />
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
              <span className="text-slate-500 text-xs">Invoice total: </span>
              <span className="font-mono font-semibold text-slate-800">
                {form.service_fee_currency} {parseFloat(form.service_fee_amount || 0).toFixed(2)}
              </span>
            </div>

            <p className="text-xs text-slate-400 mb-4">
              This creates a manual invoice and links it to this provisional itinerary. The invoice follows the existing accounting workflow.
            </p>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowInvoiceModal(false)} className="text-xs">Cancel</Button>
              <Button size="sm" onClick={createInvoice} disabled={invCreating || !invDate}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs">
                {invCreating ? "Creating…" : "Create Invoice"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
